import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';
import { BarChart2 } from 'lucide-react';
import { getCpaClassName, getCosClassName } from '../utils/metricsUtils';

// Helper pour formater les nombres de manière sécurisée
const safeFormat = (value, type = 'number', decimals = 0) => {
  if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
    return type === 'currency' ? '0€' : type === 'percent' ? '0%' : '0';
  }
  if (type === 'currency') {
    return `${Number(value).toFixed(decimals)}€`;
  }
  if (type === 'percent') {
    return `${Number(value).toFixed(decimals)}%`;
  }
  return Number(value).toLocaleString('fr-FR', { maximumFractionDigits: decimals });
};

/**
 * Composant pour afficher la répartition Performance Max par réseau/canal
 * Avec les mêmes KPIs que le tableau des campagnes Performance Max
 */
const PerformanceMaxNetworksTable = React.memo(({
  selectedAccount,
  selectedPeriod,
  customStartDate,
  customEndDate,
  token,
  setError,
  pmaxCampaignFilter,
  pmaxLabelsFilter,
  campaignLabelsMap,
  performanceMaxChannelsData,
  isPmaxMetricVisible,
  moduleId = 'pmax_networks'
}) => {
  const [networksData, setNetworksData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });

  // Calculer les IDs des campagnes filtrées
  const getFilteredCampaignIds = () => {
    if (!pmaxCampaignFilter && (!pmaxLabelsFilter || pmaxLabelsFilter.length === 0)) {
      return null; // Pas de filtre actif
    }

    if (!performanceMaxChannelsData || performanceMaxChannelsData.length === 0) {
      return null;
    }

    const filteredIds = performanceMaxChannelsData
      .filter(campaign => {
        const campaignId = (campaign.id || campaign.campaignId || '').toString();
        const campaignName = campaign.name || campaign.campaignName || '';

        // Filtre par campagne spécifique
        if (pmaxCampaignFilter) {
          const filterId = (pmaxCampaignFilter.id || pmaxCampaignFilter.campaignId || '').toString();
          const filterName = pmaxCampaignFilter.name || pmaxCampaignFilter.campaignName || '';
          if (campaignId !== filterId && campaignName !== filterName) {
            return false;
          }
        }

        // Filtre par labels
        if (pmaxLabelsFilter && pmaxLabelsFilter.length > 0) {
          const labels = (campaignLabelsMap && campaignLabelsMap[campaignId]) || [];
          if (!pmaxLabelsFilter.some(label => labels.includes(label))) {
            return false;
          }
        }

        return true;
      })
      .map(c => c.id || c.campaignId)
      .filter(Boolean);

    return filteredIds.length > 0 ? filteredIds : null;
  };

  useEffect(() => {
    if (!selectedAccount || !selectedPeriod || !token) return;

    const loadNetworksData = async () => {
      try {
        setLoading(true);
        const filteredCampaignIds = getFilteredCampaignIds();
        console.log('[PMAX-NETWORKS-UI] Chargement des données par canal...', filteredCampaignIds ? `(${filteredCampaignIds.length} campagnes filtrées)` : '');

        const requestBody = {
          sources: [selectedAccount.id],
          period: selectedPeriod
        };

        if (selectedPeriod === 'CUSTOM') {
          if (!customStartDate || !customEndDate) return;
          requestBody.customStartDate = customStartDate;
          requestBody.customEndDate = customEndDate;
        }

        // Ajouter les campaignIds filtrés si un filtre est actif
        if (filteredCampaignIds) {
          requestBody.campaignIds = filteredCampaignIds;
        }

        const response = await axios.post(
          `${config.API_URL}/api/dashboard/components/performance-max-networks`,
          requestBody,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (response.data.success && response.data.data) {
          setNetworksData(response.data.data);
          setIsDataLoaded(true);
          console.log('[PMAX-NETWORKS-UI] Données chargées:', response.data.data.channels?.length, 'canaux');
        } else {
          console.error('[PMAX-NETWORKS-UI] Erreur API:', response.data);
        }
      } catch (error) {
        console.error('[PMAX-NETWORKS-UI] Erreur:', error);
        setError?.('Erreur lors du chargement des données par canal');
      } finally {
        setLoading(false);
      }
    };

    loadNetworksData();
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, token, setError, pmaxCampaignFilter, pmaxLabelsFilter, performanceMaxChannelsData?.length]);

  // Fonction de gestion du tri
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  // Fonction pour trier les données
  const getSortedChannels = (channels) => {
    if (!channels) return [];

    return [...channels].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'channelName':
          aValue = a.channelName || '';
          bValue = b.channelName || '';
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        case 'ctr':
          aValue = a.impressions > 0 ? (a.clicks / a.impressions * 100) : 0;
          bValue = b.impressions > 0 ? (b.clicks / b.impressions * 100) : 0;
          break;
        case 'cpc':
          aValue = a.clicks > 0 ? (a.cost / a.clicks) : Number.MAX_VALUE;
          bValue = b.clicks > 0 ? (b.cost / b.clicks) : Number.MAX_VALUE;
          break;
        case 'conversionRate':
          aValue = a.clicks > 0 ? (a.conversions / a.clicks * 100) : 0;
          bValue = b.clicks > 0 ? (b.conversions / b.clicks * 100) : 0;
          break;
        case 'cpa':
          aValue = a.conversions > 0 ? (a.cost / a.conversions) : Number.MAX_VALUE;
          bValue = b.conversions > 0 ? (b.cost / b.conversions) : Number.MAX_VALUE;
          break;
        case 'cos':
          aValue = a.conversionsValue > 0 ? (a.cost / a.conversionsValue * 100) : Number.MAX_VALUE;
          bValue = b.conversionsValue > 0 ? (b.cost / b.conversionsValue * 100) : Number.MAX_VALUE;
          break;
        default:
          aValue = a[sortConfig.key] || 0;
          bValue = b[sortConfig.key] || 0;
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Icône de tri
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  if (loading) {
    return (
      <div className="campaigns-table-section">
        <div className="section-header">
          <h4><BarChart2 size={16} />&nbsp;Performance par canal</h4>
        </div>
        <div style={{ textAlign: 'center', padding: '32px', color: '#6b7280' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
          Chargement des données par canal...
        </div>
      </div>
    );
  }

  if (!isDataLoaded || !networksData?.channels?.length) {
    return null;
  }

  const { channels, totals } = networksData;
  const sortedChannels = getSortedChannels(channels);

  // Calculer les totaux
  const totalCTR = totals?.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
  const totalCPC = totals?.clicks > 0 ? (totals.cost / totals.clicks) : 0;
  const totalConversionRate = totals?.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0;
  const totalCPA = totals?.conversions > 0 ? (totals.cost / totals.conversions) : 0;
  const totalCOS = totals?.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0;

  // Couleurs par canal
  const channelColors = {
    google_search: '#4285f4',      // Bleu Google
    search_partners: '#5f6368',    // Gris foncé
    shopping: '#34a853',           // Vert Google
    youtube: '#ff0000',            // Rouge YouTube
    display: '#fbbc04',            // Jaune Google
    gmail: '#ea4335',              // Rouge Gmail
    discover: '#ff6f00',           // Orange Discover
    maps: '#34a853',               // Vert Maps
    google_tv: '#9334e6',          // Violet
    cross_network: '#9334e6',      // Violet
    mixed: '#ff6d01',              // Orange
    unspecified: '#dadce0',        // Gris clair
    other: '#9ca3af'               // Gris
  };

  const accountId = selectedAccount?.id || 'global';

  return (
    <div className="campaigns-table-section">
      <div className="section-header">
        <h4><BarChart2 size={16} />&nbsp;Performance par canal ({sortedChannels.length})</h4>
      </div>

      {/* Graphique en barres horizontales */}
      <div style={{ marginBottom: '20px', padding: '0 16px' }}>
        <div style={{
          display: 'flex',
          height: '32px',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#f3f4f6'
        }}>
          {channels.map((channel) => (
            <div
              key={channel.channel}
              style={{
                width: `${channel.costShare || 0}%`,
                backgroundColor: channelColors[channel.channel] || '#9ca3af',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '12px',
                fontWeight: '600',
                minWidth: (channel.costShare || 0) > 5 ? 'auto' : '0',
                transition: 'width 0.3s ease'
              }}
              title={`${channel.channelName}: ${(channel.costShare || 0).toFixed(1)}%`}
            >
              {(channel.costShare || 0) > 10 && `${(channel.costShare || 0).toFixed(0)}%`}
            </div>
          ))}
        </div>

        {/* Légende */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          marginTop: '12px',
          justifyContent: 'center'
        }}>
          {channels.map(channel => (
            <div key={channel.channel} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '3px',
                backgroundColor: channelColors[channel.channel] || '#9ca3af'
              }}></div>
              <span style={{ fontSize: '13px', color: '#4b5563' }}>
                {channel.channelIcon} {channel.channelName} ({(channel.costShare || 0).toFixed(1)}%)
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Tableau des métriques */}
      <div className="campaigns-table-wrapper">
        <table className="campaigns-table" style={{ width: '100%', fontSize: '14px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
              <th
                onClick={() => handleSort('channelName')}
                className="sortable"
                style={{ textAlign: 'left', padding: '8px', minWidth: '150px', cursor: 'pointer' }}
              >
                Canal{getSortIcon('channelName')}
              </th>
              {isPmaxMetricVisible('impressions') && (
                <th onClick={() => handleSort('impressions')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                  Impr.{getSortIcon('impressions')}
                </th>
              )}
              {isPmaxMetricVisible('ctr') && (
                <th onClick={() => handleSort('ctr')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                  CTR{getSortIcon('ctr')}
                </th>
              )}
              {isPmaxMetricVisible('clics') && (
                <th onClick={() => handleSort('clicks')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                  Clics{getSortIcon('clicks')}
                </th>
              )}
              {isPmaxMetricVisible('cpc') && (
                <th onClick={() => handleSort('cpc')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                  CPC{getSortIcon('cpc')}
                </th>
              )}
              {isPmaxMetricVisible('cout') && (
                <th onClick={() => handleSort('cost')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                  Coût{getSortIcon('cost')}
                </th>
              )}
              {isPmaxMetricVisible('taux_conv') && (
                <th onClick={() => handleSort('conversionRate')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                  Taux Conv.{getSortIcon('conversionRate')}
                </th>
              )}
              {isPmaxMetricVisible('conv') && (
                <th onClick={() => handleSort('conversions')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                  Conv.{getSortIcon('conversions')}
                </th>
              )}
              {isPmaxMetricVisible('cpa') && (
                <th onClick={() => handleSort('cpa')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                  CPA{getSortIcon('cpa')}
                </th>
              )}
              {isPmaxMetricVisible('valeur_conv') && (
                <th onClick={() => handleSort('conversionsValue')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                  Valeur Conv.{getSortIcon('conversionsValue')}
                </th>
              )}
              {isPmaxMetricVisible('cos') && (
                <th onClick={() => handleSort('cos')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                  COS{getSortIcon('cos')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedChannels.map((channel) => {
              const channelCPA = channel.conversions > 0 ? (channel.cost / channel.conversions) : 0;
              const channelCOS = channel.conversionsValue > 0 ? (channel.cost / channel.conversionsValue * 100) : 0;
              const channelCTR = channel.impressions > 0 ? (channel.clicks / channel.impressions * 100) : 0;
              const channelCPC = channel.clicks > 0 ? (channel.cost / channel.clicks) : 0;
              const channelConvRate = channel.clicks > 0 ? (channel.conversions / channel.clicks * 100) : 0;

              return (
                <tr key={channel.channel} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '12px 8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: channelColors[channel.channel] || '#9ca3af'
                      }}></div>
                      <span style={{ fontWeight: '500' }}>
                        {channel.channelIcon} {channel.channelName}
                      </span>
                    </div>
                  </td>
                  {isPmaxMetricVisible('impressions') && (
                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                      {safeFormat(channel.impressions)}
                    </td>
                  )}
                  {isPmaxMetricVisible('ctr') && (
                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                      {safeFormat(channelCTR, 'percent', 2)}
                    </td>
                  )}
                  {isPmaxMetricVisible('clics') && (
                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                      {safeFormat(channel.clicks)}
                    </td>
                  )}
                  {isPmaxMetricVisible('cpc') && (
                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                      {safeFormat(channelCPC, 'currency', 2)}
                    </td>
                  )}
                  {isPmaxMetricVisible('cout') && (
                    <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                      {safeFormat(channel.cost, 'currency', 2)}
                    </td>
                  )}
                  {isPmaxMetricVisible('taux_conv') && (
                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                      {safeFormat(channelConvRate, 'percent', 2)}
                    </td>
                  )}
                  {isPmaxMetricVisible('conv') && (
                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                      {safeFormat(channel.conversions, 'number', 1)}
                    </td>
                  )}
                  {isPmaxMetricVisible('cpa') && (
                    <td
                      className={`numeric ${getCpaClassName(channelCPA, accountId, moduleId)}`}
                      style={{ padding: '12px 8px', textAlign: 'right' }}
                    >
                      {channel.conversions >= 0 ? safeFormat(channelCPA, 'currency', 2) : '-'}
                    </td>
                  )}
                  {isPmaxMetricVisible('valeur_conv') && (
                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                      {safeFormat(channel.conversionsValue, 'currency', 2)}
                    </td>
                  )}
                  {isPmaxMetricVisible('cos') && (
                    <td
                      className={`numeric ${getCosClassName(channelCOS, accountId, moduleId)}`}
                      style={{ padding: '12px 8px', textAlign: 'right' }}
                    >
                      {channel.conversionsValue > 0 ? safeFormat(channelCOS, 'percent', 1) : '∞'}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="total-row" style={{ borderTop: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
              <td style={{ padding: '12px 8px', fontWeight: '700' }}>TOTAL</td>
              {isPmaxMetricVisible('impressions') && (
                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                  {safeFormat(totals?.impressions)}
                </td>
              )}
              {isPmaxMetricVisible('ctr') && (
                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                  {safeFormat(totalCTR, 'percent', 2)}
                </td>
              )}
              {isPmaxMetricVisible('clics') && (
                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                  {safeFormat(totals?.clicks)}
                </td>
              )}
              {isPmaxMetricVisible('cpc') && (
                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                  {safeFormat(totalCPC, 'currency', 2)}
                </td>
              )}
              {isPmaxMetricVisible('cout') && (
                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '700' }}>
                  {safeFormat(totals?.cost, 'currency', 2)}
                </td>
              )}
              {isPmaxMetricVisible('taux_conv') && (
                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                  {safeFormat(totalConversionRate, 'percent', 2)}
                </td>
              )}
              {isPmaxMetricVisible('conv') && (
                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                  {safeFormat(totals?.conversions, 'number', 1)}
                </td>
              )}
              {isPmaxMetricVisible('cpa') && (
                <td
                  className={`numeric ${getCpaClassName(totalCPA, accountId, moduleId)}`}
                  style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}
                >
                  {totals?.conversions >= 0 ? safeFormat(totalCPA, 'currency', 2) : '-'}
                </td>
              )}
              {isPmaxMetricVisible('valeur_conv') && (
                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                  {safeFormat(totals?.conversionsValue, 'currency', 2)}
                </td>
              )}
              {isPmaxMetricVisible('cos') && (
                <td
                  className={`numeric ${getCosClassName(totalCOS, accountId, moduleId)}`}
                  style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '700' }}
                >
                  {totals?.conversionsValue > 0 ? safeFormat(totalCOS, 'percent', 1) : '∞'}
                </td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
});

export default PerformanceMaxNetworksTable;
